/* eslint-disable react/no-access-state-in-setstate -- Rule crashes on this class component under ESLint 7. */
/**
 * @typedef {import('../../../../types/AuthContext').default} Auth
 * @typedef {import('../../../Agent/GetContact').default} ContactData
 * @typedef {import('../../../Agent/agent-service').Contact} ContactFormValues
 * @typedef {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties'][0]} ContactProperty
 * @typedef {import('../../../shared/AdministratorService/GetsAgentsByGroupIds').Agent} Agent
 * @typedef {import('./EditContact').Props} Props
 * @typedef {import('./EditContact').State} State
 */

import { AuthContext } from '../../../../shared/AuthProvider';
import {
	assignContactTags,
	createContactActivity,
	createContactTag,
	getContact,
	getContactActivity,
	getContactHomeownerProperties,
	getContactTags,
} from '../../../Agent/agent-service';
import { Redirect } from 'react-router';
import { Link } from 'react-router-dom';
import { translate } from '../../../Internationalization';
import { withContactService } from '../../../../service-container';
import { withSettings } from '../../../../shared/SettingProvider/SettingProvider';
import BaseButton from '../../../../shared/BaseButton/BaseButton';
import captureError from '../../../../utils/captureError';
import ContactFinancesAndValuation from '../../../shared/ContactFinancesAndValuation';
import ContactForm from '../../../Admin/Users/Contacts/ContactForm';
import ContactLimitPopup from '../../../shared/ContactLimitPopup/ContactLimitPopup';
import FormErrorMessages from '../../../../shared/Forms/Messages/FormErrorMessages';
import FormSuccessMessages from '../../../../shared/Forms/Messages/FormSuccessMessages';
import UpgradeOverlay from '../../../shared/UpgradeOverlay';
import flags from '../../../../shared/Flags';
import formatDateForDisplay from '../../../../utils/formatDateForDisplay';
import formatDateStringForDatepicker from '../../../../utils/formatDateStringForDatepicker';
import {
	calculateEstimatedCurrentBalance,
	calculateEstimatedCurrentEquity,
	convertFinanceWidgetCentsToDollars,
} from '../../../shared/FinanceWidgets/financeWidgetAttributes';
import images from '../../../../config/local/images';
import LinkButton from '../../../../shared/LinkButton';
import Loading from '../../../../shared/Loading';
import paths from '../../../../config/local/paths';
import React from 'react';
import ResendInviteButton from '../../../shared/ResendInviteButton';

/** @extends {React.Component<Props, State>} */
class EditContact extends React.Component {
	/** @type {Auth} */
	context = /** @type {Auth} */ ({});

	/**
	 * @param {Props} props
	 */
	constructor(props) {
		super(props);
		/** @type {State} */
		this.state = {
			activities: [],
			activityComment: '',
			activityErrorMessage: null,
			activitySuccessMessage: '',
			availableTags: [],
			contactData: null,
			errorMessage: null,
			formComplete: false,
			initialGroupValues: null,
			isEditing: false,
			isGettingData: true,
			isGettingProperties: false,
			isManagingTags: false,
			isSavingActivity: false,
			isSavingTags: false,
			newTagName: '',
			properties: [],
			resendInviteMessage: null,
			selectedPropertyId: '',
			showLimitContactPopup: false,
			tagErrorMessage: null,
			tagIdsDraft: [],
			tagSuccessMessage: '',
		};

		this.handleActivityCommentChange =
			this.#handleActivityCommentChange.bind(this);
		this.handleCreateActivity = this.#handleCreateActivity.bind(this);
		this.handleCreateTag = this.#handleCreateTag.bind(this);
		this.handleRemoveTag = this.#handleRemoveTag.bind(this);
		this.handleResendInvite = this.handleResendInvite.bind(this);
		this.handleSaveTags = this.#handleSaveTags.bind(this);
		this.handleContactMortgageSaved =
			this.#handleContactMortgageSaved.bind(this);
		this.handlePropertySelect = this.#handlePropertySelect.bind(this);
		this.handleTagDraftToggle = this.#handleTagDraftToggle.bind(this);
		this.handleTagNameChange = this.#handleTagNameChange.bind(this);
		this.handleToggleEdit = this.handleToggleEdit.bind(this);
		this.handleToggleTagManager = this.#handleToggleTagManager.bind(this);
		this._handleLimitContactClose =
			this._handleLimitContactClose.bind(this);
		this._handleSubmit = this._handleSubmit.bind(this);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		try {
			const accessToken = /** @type {{accessToken: string}} */ (
				this.context
			).accessToken;

			const contactResponse = await getContact(
				this.props.match.params.contactId,
				accessToken
			);
			const [agentList, activities, availableTags] = await Promise.all([
				this.#getAvailableAgents().catch((error) => {
					captureError(error);

					return [];
				}),
				getContactActivity(
					this.props.match.params.contactId,
					accessToken
				).catch((error) => {
					captureError(error);

					return [];
				}),
				getContactTags(accessToken, {
					contactId: this.props.match.params.contactId,
				}).catch((error) => {
					captureError(error);

					return [];
				}),
			]);
			const properties = contactResponse.homeownerId
				? await getContactHomeownerProperties(
						contactResponse.homeownerId,
						accessToken
				  ).catch((error) => {
						captureError(error);

						return [];
				  })
				: [];
			const selectedProperty =
				EditContact.#getDefaultProperty(properties);

			const initialGroupValues = EditContact.#getInitialGroupValues(
				contactResponse,
				agentList
			);

			const moveDate = contactResponse?.moveDate
				? formatDateStringForDatepicker(contactResponse?.moveDate)
				: '';
			const closeDate = contactResponse?.closeDate
				? formatDateStringForDatepicker(contactResponse?.closeDate)
				: '';

			this.setState({
				activities,
				availableTags,
				contactData: { ...contactResponse, closeDate, moveDate },
				initialGroupValues,
				isGettingData: false,
				properties,
				selectedPropertyId: selectedProperty?.id ?? '',
				tagIdsDraft: contactResponse?.tagIds ?? [],
			});
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {ContactData} contactData
	 * @param {Agent[]} agentList
	 * @returns {{agent: Agent | null, agentList: Agent[]}}
	 */
	static #getInitialGroupValues(contactData, agentList) {
		const agent = agentList.find(
			/**
			 * @param  {Agent} groupAgent
			 * @returns {boolean}
			 */
			(groupAgent) => groupAgent.id === contactData.agentId
		);

		return {
			agent: agent ?? null,
			agentList,
		};
	}

	/**
	 * @returns {Promise<Agent[]>}
	 */
	async #getAvailableAgents() {
		/** @type {{id: string; name: string}[]} */
		const allowedGroups =
			await this.props.administratorService.getAllowedGroups(50);

		if (allowedGroups.length === 0) {
			return [];
		}

		const agents =
			await this.props.administratorService.getAgentsByGroupIds(
				allowedGroups.map((group) => group.id)
			);

		return EditContact.#dedupeAgents(agents);
	}

	/**
	 * @param {Agent[]} agentList
	 * @returns {Agent[]}
	 */
	static #dedupeAgents(agentList) {
		const seen = new Set();

		return agentList.filter((agent) => {
			if (!agent?.id || seen.has(agent.id)) {
				return false;
			}

			seen.add(agent.id);

			return true;
		});
	}

	/**
	 * @param {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties']} properties
	 * @returns {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties'][0] | undefined}
	 */
	static #getDefaultProperty(properties) {
		return (
			properties.find((property) => property.isPrimary) ??
			properties.find(Boolean)
		);
	}

	/**
	 * @param {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties'][0]} property
	 * @param {object} details
	 * @returns {import('../../../Agent/Contacts/EditContactState').default['initialValues']['properties'][0]}
	 */
	static #mergeMortgageDetails(property, details) {
		return {
			...property,
			details: {
				...property.details,
				...details,
			},
		};
	}

	/**
	 * @protected
	 * @param {ContactFormValues} values
	 * @returns {Promise<void>}
	 */
	async _handleSubmit(values) {
		try {
			const accessToken = /** @type {{accessToken: string}} */ (
				this.context
			).accessToken;

			await this.props.contactService.updateContact(
				this.props.match.params.contactId,
				accessToken,
				values
			);

			this.setState({
				errorMessage: null,
				formComplete: true,
				isEditing: false,
			});
		} catch (error) {
			// @ts-ignore -- status code added to error obj
			if (error?.statusCode === 403) {
				this.setState({
					showLimitContactPopup: true,
				});
				return;
			}
			captureError(error);
			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {React.ChangeEvent<HTMLSelectElement>} event
	 * @returns {Promise<void>}
	 */
	async handleResendInvite(event) {
		event.preventDefault();
		if (!this.state.contactData?.id) {
			return;
		}

		try {
			await this.props.contactService.inviteContact(
				this.state.contactData?.id
			);

			this.setState((prevState) => ({
				resendInviteMessage: translate(
					'admin.pages.contacts.edit.invite.success',
					prevState.contactData?.firstName ?? '',
					prevState.contactData?.lastName ?? ''
				),
			}));
		} catch (error) {
			captureError(error);

			this.setState({
				resendInviteMessage: translate(
					'admin.pages.contacts.edit.invite.error'
				),
			});
		}
	}

	/**
	 * @returns {void}
	 */
	handleToggleEdit() {
		this.setState((prevState) => ({
			activityErrorMessage: null,
			activitySuccessMessage: '',
			errorMessage: null,
			isEditing: !prevState.isEditing,
		}));
	}

	/**
	 * @param {string | null | undefined} homeownerId
	 * @returns {Promise<void>}
	 */
	async #setContactProperties(homeownerId) {
		if (!homeownerId) {
			return;
		}

		this.setState({ isGettingProperties: true });

		try {
			const properties = await getContactHomeownerProperties(
				homeownerId,
				this.context.accessToken
			);
			const selectedProperty =
				EditContact.#getDefaultProperty(properties);

			this.setState({
				isGettingProperties: false,
				properties,
				selectedPropertyId: selectedProperty?.id ?? '',
			});
		} catch (error) {
			captureError(error);
			this.setState({
				isGettingProperties: false,
			});
		}
	}

	/**
	 * @param {string} propertyId
	 * @returns {void}
	 */
	#handlePropertySelect(propertyId) {
		this.setState({
			selectedPropertyId: propertyId,
		});
	}

	/**
	 * @param {string} propertyId
	 * @param {object} mortgageDetails
	 * @returns {Promise<void>}
	 */
	async #handleContactMortgageSaved(propertyId, mortgageDetails) {
		await this.#setContactProperties(this.state.contactData?.homeownerId);

		this.setState((prevState) => ({
			properties: prevState.properties.map((property) => {
				if (property.id !== propertyId) {
					return property;
				}

				return EditContact.#mergeMortgageDetails(
					property,
					mortgageDetails
				);
			}),
			selectedPropertyId: propertyId,
		}));
	}

	/**
	 * @param {React.ChangeEvent<HTMLTextAreaElement>} event
	 * @returns {void}
	 */
	#handleActivityCommentChange(event) {
		this.setState({
			activityComment: event.target.value,
			activityErrorMessage: null,
			activitySuccessMessage: '',
		});
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleCreateActivity() {
		const description = this.state.activityComment.trim();

		if (!description) {
			this.setState({
				activityErrorMessage: translate(
					'agent.pages.contacts.edit.activity.create_required'
				),
				activitySuccessMessage: '',
			});
			return;
		}

		try {
			this.setState({
				activityErrorMessage: null,
				activitySuccessMessage: '',
				isSavingActivity: true,
			});

			await createContactActivity(
				this.props.match.params.contactId,
				description,
				this.context.accessToken
			);

			const activities = await getContactActivity(
				this.props.match.params.contactId,
				this.context.accessToken
			);

			this.setState({
				activities,
				activityComment: '',
				activityErrorMessage: null,
				activitySuccessMessage: translate(
					'agent.pages.contacts.edit.activity.create_success'
				),
				isSavingActivity: false,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				activityErrorMessage: translate(
					'agent.pages.contacts.edit.activity.create_error'
				),
				activitySuccessMessage: '',
				isSavingActivity: false,
			});
		}
	}

	/**
	 * @returns {void}
	 */
	#handleToggleTagManager() {
		this.setState((prevState) => ({
			isManagingTags: !prevState.isManagingTags,
			newTagName: '',
			tagErrorMessage: null,
			tagIdsDraft: prevState.isManagingTags
				? prevState.contactData?.tagIds ?? []
				: [...(prevState.contactData?.tagIds ?? [])],
			tagSuccessMessage: prevState.isManagingTags
				? prevState.tagSuccessMessage
				: '',
		}));
	}

	/**
	 * @param {string} tagId
	 * @returns {void}
	 */
	#handleTagDraftToggle(tagId) {
		this.setState((prevState) => {
			const tagIdSet = new Set(prevState.tagIdsDraft);

			if (tagIdSet.has(tagId)) {
				tagIdSet.delete(tagId);
			} else {
				tagIdSet.add(tagId);
			}

			return {
				tagErrorMessage: null,
				tagIdsDraft: [...tagIdSet],
				tagSuccessMessage: '',
			};
		});
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 * @returns {void}
	 */
	#handleTagNameChange(event) {
		this.setState({
			newTagName: event.target.value,
			tagErrorMessage: null,
			tagSuccessMessage: '',
		});
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleCreateTag() {
		const tagName = this.state.newTagName.trim();

		if (!tagName) {
			this.setState({
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.create_required'
				),
			});
			return;
		}

		try {
			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			const tag = await createContactTag(
				tagName,
				this.context.accessToken,
				{ contactId: this.props.match.params.contactId }
			);
			const nextTagIds = [
				...new Set([...(this.state.contactData?.tagIds ?? []), tag.id]),
			];

			await assignContactTags(
				this.props.match.params.contactId,
				nextTagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				availableTags: [...prevState.availableTags, tag],
				contactData: prevState.contactData
					? {
							...prevState.contactData,
							tagIds: nextTagIds,
					  }
					: prevState.contactData,
				isManagingTags: false,
				isSavingTags: false,
				newTagName: '',
				tagIdsDraft: nextTagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.create_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.create_error'
				),
			});
		}
	}

	/**
	 * @param {string} tagId
	 * @returns {Promise<void>}
	 */
	async #handleRemoveTag(tagId) {
		try {
			const currentTagIds = this.state.contactData?.tagIds ?? [];
			const nextTagIds = currentTagIds.filter(
				(currentTagId) => currentTagId !== tagId
			);

			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			await assignContactTags(
				this.props.match.params.contactId,
				nextTagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				contactData: prevState.contactData
					? {
							...prevState.contactData,
							tagIds: nextTagIds,
					  }
					: prevState.contactData,
				isManagingTags: false,
				isSavingTags: false,
				tagIdsDraft: nextTagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.save_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.remove_error'
				),
			});
		}
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #handleSaveTags() {
		try {
			this.setState({
				isSavingTags: true,
				tagErrorMessage: null,
				tagSuccessMessage: '',
			});

			const tagIds = [...new Set(this.state.tagIdsDraft)];

			await assignContactTags(
				this.props.match.params.contactId,
				tagIds,
				this.context.accessToken
			);

			this.setState((prevState) => ({
				contactData: prevState.contactData
					? {
							...prevState.contactData,
							tagIds,
					  }
					: prevState.contactData,
				isManagingTags: false,
				isSavingTags: false,
				tagIdsDraft: tagIds,
				tagSuccessMessage: translate(
					'agent.pages.contacts.edit.tags.save_success'
				),
			}));
		} catch (error) {
			captureError(error);
			this.setState({
				isSavingTags: false,
				tagErrorMessage: translate(
					'agent.pages.contacts.edit.tags.save_error'
				),
			});
		}
	}

	/**
	 * @protected
	 * @returns {void}
	 */
	_handleLimitContactClose() {
		this.setState({ showLimitContactPopup: false });
	}

	/**
	 * @returns {JSX.Element}
	 */
	static #renderContactPopupContent() {
		const contactLimit = 'global.contact_limit';
		const contactCap = process.env.REACT_APP_CONTACT_CAP ?? '100';

		return (
			<React.Fragment>
				<div>
					{translate(
						`global.contact_limit.assign.header`,
						contactCap
					)}{' '}
					<span className="font-bold">
						{translate(`${contactLimit}.assign.body`)}
					</span>
				</div>

				<div>
					{translate(
						`global.contact_limit.assign.group_footer`,
						contactCap
					)}
				</div>
			</React.Fragment>
		);
	}

	/**
	 * @param {string | null | undefined} value
	 * @returns {string}
	 */
	static #formatValue(value) {
		return value || '—';
	}

	/**
	 * @param {number | null | undefined} value
	 * @returns {string}
	 */
	static #formatCurrency(value) {
		return value === null || value === undefined
			? ''
			: value.toLocaleString('en-US', {
					currency: 'USD',
					maximumFractionDigits: 0,
					style: 'currency',
			  });
	}

	/**
	 * @param {ContactProperty} property
	 * @returns {string}
	 */
	static #formatPropertyAddress(property) {
		const address = [
			property.address.streetAddress1,
			property.address.streetAddress2,
			property.address.city,
			property.address.state,
			property.address.postalCode,
			property.address.country,
		]
			.filter(Boolean)
			.join(', ');

		return address || property.title || 'Property';
	}

	/**
	 * @param {ContactProperty} property
	 * @returns {string}
	 */
	static #formatPropertyCategory(property) {
		const labels = new Map([
			[
				'former_home',
				translate(
					'homeowner.pages.properties.form.category_options.former_home'
				),
			],
			[
				'friends_or_family_home',
				translate(
					'homeowner.pages.properties.form.category_options.friends_or_family_home'
				),
			],
			[
				'primary_home',
				translate(
					'homeowner.pages.properties.form.category_options.primary_home'
				),
			],
			[
				'second_home',
				translate(
					'homeowner.pages.properties.form.category_options.second_home'
				),
			],
			[
				'watch_list',
				translate(
					'homeowner.pages.properties.form.category_options.watch_list'
				),
			],
		]);

		return labels.get(property.category ?? '') ?? property.category ?? '—';
	}

	/**
	 * @param {ContactProperty} property
	 * @returns {number | null}
	 */
	static #getAverageValuationValue(property) {
		const values = property.valuations
			.map((valuation) => valuation.valueMean)
			.filter((value) => value !== null && value !== undefined);

		if (!values.length) {
			return null;
		}

		return convertFinanceWidgetCentsToDollars(
			values.reduce((sum, value) => sum + value, 0) / values.length
		);
	}

	/**
	 * @param {ContactProperty} property
	 * @returns {string}
	 */
	static #formatPropertyEquity(property) {
		const averageValue = EditContact.#getAverageValuationValue(property);
		const currentBalance = calculateEstimatedCurrentBalance(
			property.details
		);

		return EditContact.#formatCurrency(
			calculateEstimatedCurrentEquity(averageValue, currentBalance)
		);
	}

	/**
	 * @param {number | string | null | undefined} value
	 * @returns {number}
	 */
	static #normalizeTier(value) {
		const tier = Number(value);

		return Number.isFinite(tier) ? tier : 0;
	}

	/**
	 * @returns {number}
	 */
	#getCurrentSubscriptionTier() {
		/** @type {Record<string, any>} */
		const userTiers = this.props.settings?.userTiers ?? {};

		return EditContact.#normalizeTier(
			userTiers.managerTier ?? userTiers.manager_tier
		);
	}

	/**
	 * @param {string} flag
	 * @returns {boolean}
	 */
	#isSubscriptionFlagEnabled(flag) {
		return this.props.settings.isEnabled(flag, 'manager_tier');
	}

	/**
	 * @param {string} propertyId
	 * @returns {string}
	 */
	#getPropertyFinancesPath(propertyId) {
		return paths.app.admin.contacts.propertyFinances
			.replace(':contactId', this.props.match.params.contactId)
			.replace(':propertyId', propertyId);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderContactPropertiesSummary() {
		if (!this.state.properties.length) {
			return null;
		}

		const canViewAddresses = this.#isSubscriptionFlagEnabled(
			flags.manager.contactAddress
		);
		const canViewFinancialDetails = this.#isSubscriptionFlagEnabled(
			flags.manager.contactAddressDetails
		);
		const gridTemplateColumns =
			'minmax(20rem, 2fr) minmax(10rem, 1fr) minmax(7rem, 0.7fr) minmax(8rem, 0.75fr) minmax(7rem, 0.7fr)';
		const addressUpgradeMessage =
			'Property addresses are available on Starter plans and above.';
		const financialDetailsUpgradeMessage =
			'Category, value, interest rate, and equity are available on Standard plans and above.';

		if (canViewAddresses && !canViewFinancialDetails) {
			return (
				<section className="mb-6">
					<h2 className="h-ms-5 mb-1">Properties</h2>
					<div
						className="grid text-ms-0 leading-snug gap-x-6"
						style={{
							gridTemplateColumns: 'minmax(20rem, 1fr) 2fr',
						}}
					>
						<div className="border-b border-grey-300 pb-1 pr-4 font-subheader font-bold">
							Address
						</div>
						<div
							className="grid border-b border-grey-300 pb-1 font-subheader font-bold"
							style={{
								gridTemplateColumns: 'repeat(4, 1fr)',
							}}
						>
							<div>Category</div>
							<div>Value*</div>
							<div>Interest Rate*</div>
							<div>Equity*</div>
						</div>
						<div>
							{this.state.properties.map((property) => (
								<div
									className="border-b border-grey-100 py-1 pr-4 align-top text-info"
									key={property.id}
								>
									<Link
										className="text-info"
										to={this.#getPropertyFinancesPath(
											property.id
										)}
									>
										{EditContact.#formatPropertyAddress(
											property
										)}
									</Link>
								</div>
							))}
						</div>
						<div className="flex items-start">
							<UpgradeOverlay
								buttonText={translate(
									'agent.pages.contacts.view.upgrade_table.upgrade'
								)}
								messages={[financialDetailsUpgradeMessage]}
								title="Upgrade to view property details"
								to={paths.app.admin.groups.root}
							/>
						</div>
					</div>
				</section>
			);
		}

		return (
			<section className="mb-6">
				<h2 className="h-ms-5 mb-1">Properties</h2>
				<div className="overflow-x-auto">
					<div
						className="grid text-ms-0 leading-snug min-w-max"
						style={{ gridTemplateColumns }}
					>
						<div className="border-b border-grey-300 pb-1 pr-4 font-subheader font-bold">
							Address
						</div>
						<div className="border-b border-grey-300 pb-1 pr-4 font-subheader font-bold">
							Category
						</div>
						<div className="border-b border-grey-300 pb-1 pr-4 font-subheader font-bold">
							Value*
						</div>
						<div className="border-b border-grey-300 pb-1 pr-4 font-subheader font-bold">
							Interest Rate*
						</div>
						<div className="border-b border-grey-300 pb-1 font-subheader font-bold">
							Equity*
						</div>
						{canViewAddresses ? (
							this.state.properties.map((property) => (
								<React.Fragment key={property.id}>
									<div className="border-b border-grey-100 py-1 pr-4 align-top text-info">
										<Link
											className="text-info"
											to={this.#getPropertyFinancesPath(
												property.id
											)}
										>
											{EditContact.#formatPropertyAddress(
												property
											)}
										</Link>
									</div>
									<div className="border-b border-grey-100 py-1 pr-4">
										{canViewFinancialDetails
											? EditContact.#formatPropertyCategory(
													property
											  )
											: '—'}
									</div>
									<div className="border-b border-grey-100 py-1 pr-4 tabular-nums">
										{canViewFinancialDetails
											? EditContact.#formatValue(
													EditContact.#formatCurrency(
														EditContact.#getAverageValuationValue(
															property
														)
													)
											  )
											: '—'}
									</div>
									<div className="border-b border-grey-100 py-1 pr-4 tabular-nums">
										{canViewFinancialDetails
											? EditContact.#formatValue(
													property.details.rate ===
														null ||
														property.details
															.rate === undefined
														? ''
														: `${property.details.rate}%`
											  )
											: '—'}
									</div>
									<div className="border-b border-grey-100 py-1 tabular-nums">
										{canViewFinancialDetails
											? EditContact.#formatValue(
													EditContact.#formatPropertyEquity(
														property
													)
											  )
											: '—'}
									</div>
								</React.Fragment>
							))
						) : (
							<div style={{ gridColumn: '1 / -1' }}>
								<UpgradeOverlay
									buttonText={translate(
										'agent.pages.contacts.view.upgrade_table.upgrade'
									)}
									messages={[
										addressUpgradeMessage,
										financialDetailsUpgradeMessage,
									]}
									title="Upgrade to view property details"
									to={paths.app.admin.groups.root}
								/>
							</div>
						)}
					</div>
				</div>
			</section>
		);
	}

	/**
	 * @param {string} label
	 * @param {string | null | undefined} value
	 * @param {string} [className]
	 * @returns {JSX.Element}
	 */
	static #renderReadOnlyField(label, value, className = '') {
		return (
			<section className={className || ''}>
				<label className="input-label">{label}</label>
				<div className="min-h-[3.2rem] py-1.5 text-ms-0 leading-normal text-grey-900">
					{EditContact.#formatValue(value)}
				</div>
			</section>
		);
	}

	/**
	 * @param {string} label
	 * @param {React.ReactNode} value
	 * @returns {JSX.Element}
	 */
	static #renderCompactContactField(label, value) {
		return (
			<section>
				<h2 className="text-ms-0 font-normal leading-tight">{label}</h2>
				<div className="text-ms-0 leading-snug text-grey-900">
					{typeof value === 'string'
						? EditContact.#formatValue(value)
						: value ?? '—'}
				</div>
			</section>
		);
	}

	/**
	 * @param {string[] | undefined} tagIds
	 * @returns {{ id: string; isDefault: boolean; name: string }[]}
	 */
	#getSelectedTags(tagIds = this.state.contactData?.tagIds ?? []) {
		const selectedTagIdSet = new Set(tagIds);

		return this.state.availableTags.filter((tag) =>
			selectedTagIdSet.has(tag.id)
		);
	}

	/**
	 * @param {string | null | undefined} value
	 * @returns {string}
	 */
	static #formatActivityTimestamp(value) {
		return value ? formatDateForDisplay(value) : '—';
	}

	/**
	 * @returns {JSX.Element}
	 */
	#renderActivity() {
		return (
			<section className="h-full">
				<header className="flex items-center justify-between gap-4 mb-4">
					<h2 className="h-ms-5">
						{translate('agent.pages.contacts.edit.activity.title')}
					</h2>
				</header>

				<FormSuccessMessages
					className="mb-4"
					messages={this.state.activitySuccessMessage}
				/>
				<FormErrorMessages
					className="mb-4"
					messages={[this.state.activityErrorMessage]}
				/>

				<section className="border border-theme rounded p-4 mb-6">
					<section className="input-group mb-4">
						<label
							className="input-label"
							htmlFor="contact-activity-comment"
						>
							{translate(
								'agent.pages.contacts.edit.activity.create_label'
							)}
						</label>
						<textarea
							className="input min-h-32"
							id="contact-activity-comment"
							onChange={this.handleActivityCommentChange}
							placeholder={translate(
								'agent.pages.contacts.edit.activity.create_placeholder'
							)}
							value={this.state.activityComment}
						/>
					</section>

					<footer className="button-group">
						<BaseButton
							disabled={
								this.state.isSavingActivity ||
								!this.state.activityComment.trim()
							}
							onClick={this.handleCreateActivity}
							type="button"
						>
							{this.state.isSavingActivity
								? translate(
										'agent.pages.contacts.edit.activity.saving'
								  )
								: translate(
										'agent.pages.contacts.edit.activity.create'
								  )}
						</BaseButton>
					</footer>
				</section>

				{this.state.activities.length ? (
					<section className="border-l-2 border-theme pl-4 space-y-3">
						{this.state.activities.map((activity) => (
							<article
								className="text-sm leading-6"
								key={activity.id}
							>
								<div className="text-sm">
									{EditContact.#formatActivityTimestamp(
										activity.createdAt
									)}{' '}
									<span className="font-subheader font-bold">
										{'('}
										{activity.sourceLabel}
										{')'}
									</span>
									{' - '}
									<span>{activity.description}</span>
								</div>
							</article>
						))}
					</section>
				) : (
					<input
						className="input input-disabled"
						disabled={true}
						readOnly={true}
						type="text"
						value={translate(
							'agent.pages.contacts.edit.activity.empty'
						)}
					/>
				)}
			</section>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	// eslint-disable-next-line complexity
	#renderDetails() {
		if (!this.state.contactData) {
			return null;
		}

		const contact = this.state.contactData;
		const draftSelectedTags = this.#getSelectedTags(this.state.tagIdsDraft);
		const selectedTags = this.#getSelectedTags();

		return (
			<React.Fragment>
				<header className="flex justify-end mt-10 mb-6">
					<section className="button-group h-fit">
						<BaseButton
							onClick={this.handleToggleEdit}
							outline={true}
							type="button"
						>
							{translate('global.forms.buttons.edit')}
						</BaseButton>
						<LinkButton
							icon={images.icons.delete}
							to={paths.app.admin.contacts.view}
						>
							{translate('global.forms.buttons.cancel')}
						</LinkButton>
					</section>
				</header>

				<section
					className="grid gap-x-8 gap-y-4 mb-8"
					style={{
						gridTemplateColumns:
							'repeat(5, minmax(max-content, 1fr))',
					}}
				>
					{EditContact.#renderCompactContactField(
						'First Name',
						contact.firstName
					)}
					{EditContact.#renderCompactContactField(
						'Last Name',
						contact.lastName
					)}
					{EditContact.#renderCompactContactField(
						translate('global.forms.inputs.email.label'),
						contact.email ? (
							<a href={`mailto:${contact.email}`}>
								{contact.email}
							</a>
						) : (
							''
						)
					)}
					{EditContact.#renderCompactContactField(
						'Landline Phone',
						contact.landline
					)}
					{EditContact.#renderCompactContactField(
						'Mobile Phone',
						contact.mobile
					)}
				</section>

				{this.#renderContactPropertiesSummary()}

				<section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 items-start">
					<section>
						<header className="flex items-center justify-between gap-4 mb-4">
							<h2 className="h-ms-5">
								{translate(
									'agent.pages.contacts.edit.tags.title'
								)}
							</h2>
							<BaseButton
								disabled={this.state.isSavingTags}
								onClick={this.handleToggleTagManager}
								outline={true}
								type="button"
							>
								{this.state.isManagingTags
									? translate(
											'agent.pages.contacts.edit.tags.done'
									  )
									: translate(
											'agent.pages.contacts.edit.tags.manage'
									  )}
							</BaseButton>
						</header>

						{!!this.state.tagSuccessMessage && (
							<FormSuccessMessages
								className="mb-4"
								messages={this.state.tagSuccessMessage}
							/>
						)}
						<FormErrorMessages
							className="mb-4"
							messages={[this.state.tagErrorMessage]}
						/>

						<div className="input-group">
							{selectedTags.length > 0 ? (
								<div className="flex flex-wrap gap-3">
									{selectedTags.map((tag) => (
										<span
											className="inline-flex items-center rounded-full border border-theme px-3 py-1 text-theme font-subheader font-bold"
											key={tag.id}
										>
											<span>{tag.name}</span>
											<BaseButton
												className="ml-2 h-auto min-h-0 w-auto min-w-0 border-0 bg-transparent px-0 py-0 text-theme shadow-none"
												disabled={
													this.state.isSavingTags
												}
												onClick={() =>
													this.handleRemoveTag(tag.id)
												}
												type="button"
											>
												{'x'}
											</BaseButton>
										</span>
									))}
								</div>
							) : (
								<input
									className="input input-disabled"
									disabled={true}
									readOnly={true}
									type="text"
									value="—"
								/>
							)}
						</div>

						{this.state.isManagingTags ? (
							<section className="mt-6 border border-theme p-4 rounded">
								<h3 className="font-subheader font-bold mb-3">
									{translate(
										'agent.pages.contacts.edit.tags.assign'
									)}
								</h3>

								{this.state.availableTags.length > 0 ? (
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
										{this.state.availableTags.map((tag) => (
											<label
												className="flex items-center gap-3"
												htmlFor={`contact-tag-${tag.id}`}
												key={tag.id}
											>
												<input
													checked={this.state.tagIdsDraft.includes(
														tag.id
													)}
													id={`contact-tag-${tag.id}`}
													onChange={() =>
														this.handleTagDraftToggle(
															tag.id
														)
													}
													type="checkbox"
												/>
												<span>
													{tag.name}
													{tag.isDefault
														? ' (Default)'
														: ''}
												</span>
											</label>
										))}
									</div>
								) : null}

								{draftSelectedTags.length > 0 ? (
									<div className="mb-4 text-sm text-grey-700">
										{translate(
											'agent.pages.contacts.edit.tags.selected'
										)}
										{': '}
										{draftSelectedTags
											.map((tag) => tag.name)
											.join(', ')}
									</div>
								) : null}

								<section className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 mb-4 items-end">
									<section className="input-group mb-0">
										<label className="input-label">
											{translate(
												'agent.pages.contacts.edit.tags.create_custom'
											)}
										</label>
										<input
											className="input"
											onChange={this.handleTagNameChange}
											placeholder={translate(
												'agent.pages.contacts.edit.tags.create_placeholder'
											)}
											type="text"
											value={this.state.newTagName}
										/>
									</section>
									<BaseButton
										disabled={
											this.state.isSavingTags ||
											!this.state.newTagName.trim()
										}
										onClick={this.handleCreateTag}
										type="button"
									>
										{translate(
											'agent.pages.contacts.edit.tags.create'
										)}
									</BaseButton>
								</section>

								<footer className="button-group">
									<BaseButton
										disabled={this.state.isSavingTags}
										onClick={this.handleSaveTags}
										type="button"
									>
										{this.state.isSavingTags
											? translate(
													'agent.pages.contacts.edit.tags.saving'
											  )
											: translate(
													'agent.pages.contacts.edit.tags.save'
											  )}
									</BaseButton>
									<BaseButton
										disabled={this.state.isSavingTags}
										onClick={this.handleToggleTagManager}
										outline={true}
										type="button"
									>
										{translate(
											'global.forms.buttons.cancel'
										)}
									</BaseButton>
								</footer>
							</section>
						) : null}
					</section>

					{this.#renderActivity()}
				</section>

				{!!this.state.resendInviteMessage && (
					<div className="mb-6 text-sm text-theme">
						{this.state.resendInviteMessage}
					</div>
				)}
			</React.Fragment>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderFinancesAndValuation() {
		if (!this.state.contactData?.homeownerId) {
			return null;
		}

		const subscriptionTier = this.#getCurrentSubscriptionTier();
		const canViewAvms = this.#isSubscriptionFlagEnabled(
			flags.manager.propertyValuationQuantariumAvm
		);
		const canViewHkAvm = this.#isSubscriptionFlagEnabled(
			flags.manager.propertyValuationHkAvm
		);
		const canViewFinanceWidgets = this.#isSubscriptionFlagEnabled(
			flags.manager.contactFinanceWidgets
		);
		const canViewMortgage = this.#isSubscriptionFlagEnabled(
			flags.manager.mortgageSection
		);

		return (
			<ContactFinancesAndValuation
				accessToken={this.context.accessToken}
				agentId={this.state.contactData.agentId ?? ''}
				canViewAvms={canViewAvms}
				canViewFinanceWidgets={canViewFinanceWidgets}
				canViewHkAvm={canViewHkAvm}
				canViewMortgage={canViewMortgage}
				canViewQuantariumAvm={canViewAvms}
				contactId={this.props.match.params.contactId}
				homeownerId={this.state.contactData.homeownerId}
				isGettingProperties={this.state.isGettingProperties}
				onMortgageSaved={this.handleContactMortgageSaved}
				onPropertySelect={this.handlePropertySelect}
				properties={this.state.properties}
				selectedPropertyId={this.state.selectedPropertyId}
				subscriptionPath={paths.app.admin.groups.root}
				subscriptionTier={subscriptionTier}
				userId={this.context.userId}
			/>
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		if (this.state.formComplete) {
			return (
				<Redirect
					to={{
						pathname: paths.app.admin.contacts.view,
						state: {
							message: translate(
								'admin.pages.contacts.edit.success_message'
							),
						},
					}}
				/>
			);
		}

		if (this.state.isGettingData) {
			return <Loading />;
		}

		return (
			<React.Fragment>
				<ContactLimitPopup
					onClose={this._handleLimitContactClose}
					showPopup={this.state.showLimitContactPopup}
					title={translate(`global.contact_limit.assign.title`)}
					to={paths.app.admin.groups.root}
				>
					{EditContact.#renderContactPopupContent()}
				</ContactLimitPopup>

				{this.state.isEditing ? (
					<React.Fragment>
						<ContactForm
							additionalButtons={[
								<ResendInviteButton
									icon={images.icons.plus}
									key={0}
									onResendInvite={this.handleResendInvite}
									outline={true}
									size="large"
								/>,
							]}
							contactData={this.state.contactData}
							error={this.state?.errorMessage}
							formTitle={translate(
								'admin.pages.contacts.edit.title'
							)}
							initialGroupValues={this.state.initialGroupValues}
							onResendInvite={this.handleResendInvite}
							onSubmit={this._handleSubmit}
							resendInviteMessage={this.state.resendInviteMessage}
							submitText={translate(
								'admin.pages.contacts.edit.update'
							)}
						/>
					</React.Fragment>
				) : (
					this.#renderDetails()
				)}
			</React.Fragment>
		);
	}
}

export default /** @type {React.ComponentType<any>} */ (
	withSettings(withContactService(EditContact))
);

EditContact.contextType = AuthContext;
